import { useCallback, useEffect, useState } from 'react';
import { LeadsService } from 'service/leads.service';

type LeadFile = File | string;

type AttachmentInput =
  | File[]
  | string[]
  | null
  | {
      added?: any[];
      removed?: any[];
    };

type PresignedResponse = {
  url?: string;
  fields?: Record<string, string>;
  key?: string;
  s3Key?: string;
  uploadUrl?: string;
};

const S3_BUCKET_URL = 'https://huddle-client-files-dev.s3.us-east-1.amazonaws.com/';

const getAttachmentsArray = (attachments?: AttachmentInput): LeadFile[] => {
  if (!attachments) return [];

  if (typeof attachments === 'object' && !Array.isArray(attachments) && 'added' in attachments) {
    return Array.isArray(attachments.added) ? [...attachments.added] : [];
  }

  if (Array.isArray(attachments)) {
    return [...attachments];
  }

  return [];
};

export const useLeadFiles = (
  leadId?: string,
  attachments?: AttachmentInput,
  onUploadComplete?: (uploadedRefs: string[]) => Promise<void> | void,
) => {
  const [selectedFiles, setSelectedFiles] = useState<LeadFile[]>(getAttachmentsArray(attachments));
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);

  useEffect(() => {
    setSelectedFiles(getAttachmentsArray(attachments));
  }, []);

  const uploadSingleFile = useCallback(
    async (file: File): Promise<string> => {
      if (!leadId) throw new Error('leadId is required');

      const presigned = (await LeadsService.getLeadPresignedUrl(
        file.name,
        file.type,
        String(file.size),
        leadId,
      )) as PresignedResponse;

      if (presigned?.url && presigned?.fields) {
        const formData = new FormData();
        Object.entries(presigned.fields).forEach(([k, v]) => formData.append(k, v));
        formData.append('file', file);

        const res = await fetch(S3_BUCKET_URL, { method: 'POST', body: formData });
        if (!res.ok) throw new Error('S3 POST upload failed');

        return presigned.fields.key || presigned.key || presigned.s3Key || file.name;
      }

      if (presigned?.uploadUrl) {
        const res = await fetch(presigned.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!res.ok) throw new Error('S3 PUT upload failed');

        return presigned.s3Key || presigned.key || file.name;
      }

      throw new Error('Invalid presigned response');
    },
    [leadId],
  );

  const onDrop = useCallback(
    async (files: File[]) => {
      if (!files?.length || !leadId) return;

      const original = [...selectedFiles];
      setSelectedFiles((prev) => [...prev, ...files]);

      try {
        const uploadedRefs = await Promise.all(files.map(uploadSingleFile));
        setUploadedUrls((prev) => [...prev, ...uploadedRefs]);

        await onUploadComplete?.(uploadedRefs);

        setSelectedFiles((prev) => {
          const withoutLocalFiles = prev.filter(
            (f) =>
              !(f instanceof File && files.some((nf) => nf.name === f.name && nf.size === f.size)),
          );
          return [...withoutLocalFiles, ...uploadedRefs];
        });
      } catch (e) {
        setSelectedFiles(original);
        throw e;
      }
    },
    [leadId, onUploadComplete, selectedFiles, uploadSingleFile],
  );

  const deleteFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetFiles = useCallback(() => {
    setSelectedFiles(getAttachmentsArray(attachments));
  }, [attachments]);

  return {
    selectedFiles,
    uploadedUrls,
    setSelectedFiles,
    onDrop,
    deleteFile,
    resetFiles,
  };
};
